import { canvasToolCardModel, type CanvasToolCardModel } from "@cleo/shared/canvasToolCard";
import type { ConvexClient } from "convex/browser";
import { makeFunctionReference } from "convex/server";
import { useEffect, useMemo, useState } from "react";

import { worldWords, type CloudEvent } from "./cloudConversations";
import { useWorldConvexClient } from "./worldConvexClient";

/**
 * cleo-web (webHost.ts): the member's Canvases, read from the World itself.
 *
 * On the Desk the Context rail's Canvases lens is the room-universe catalog, read through the Mac server
 * (roomUniverseStore.ts, /api/neucleos/room-universe), so the browser host, which has no Mac, listed nothing (PR #2191
 * recorded the gap). Here the lens is the World's own catalog door, canvas:catalog (backend/convex/canvas.js
 * catalogForAccess): every Canvas of the World the person may read, as Cleo's own `catalog` action and Pocket read it,
 * live through the window's signed-in client. The World pages the catalog by canvasId; the lens follows the pages and
 * shows the Canvases newest first.
 */

export interface WebHostCanvasEntry {
  readonly canvasId: string;
  readonly memberId: string;
  readonly name: string;
  readonly type: string;
  readonly headSequence: number;
  readonly createdAt: number;
  readonly updatedAt: number;
  readonly description?: string;
}

export const CANVAS_ID = /^cnv_[0-9a-f]{24}$/u;
export const CANVAS_MEMBER_PREFIX = "world-vfs:canvas/";
/** The page the World bounds a catalog read to (lib/canvasContracts.js MAX_CANVAS_CATALOG). */
export const CATALOG_PAGE = 200;
/** The most pages the lens follows (the first live, the rest read once per change of the first): 1,000 Canvases. */
export const CATALOG_PAGES_MAX = 5;

type CatalogPage = { readonly canvases: ReadonlyArray<unknown>; readonly nextCursor: string | null };
const catalogRef = makeFunctionReference<
  "query",
  { worldKey: string; cursor?: string; limit?: number },
  CatalogPage
>("canvas:catalog");

export type WebHostCanvasCatalog =
  | { readonly status: "loading" }
  | {
      readonly status: "ready";
      readonly canvases: ReadonlyArray<WebHostCanvasEntry>;
      /** True when the World holds more Canvases than the lens followed. */
      readonly more: boolean;
    }
  | { readonly status: "unavailable"; readonly reason: string };

/** The canvasId a Canvas member id names (world-vfs:canvas/cnv_…), or null for any other member. */
export function canvasIdOfMember(memberId: string): string | null {
  if (!memberId.startsWith(CANVAS_MEMBER_PREFIX)) return null;
  const canvasId = memberId.slice(CANVAS_MEMBER_PREFIX.length);
  return CANVAS_ID.test(canvasId) ? canvasId : null;
}

/** One catalog row as the World answers it (canvasView), or null for anything else. */
export function webHostCanvasEntry(value: unknown): WebHostCanvasEntry | null {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return null;
  const row = value as Record<string, unknown>;
  const canvasId = row["canvasId"];
  const name = row["name"];
  const type = row["type"];
  const headSequence = row["headSequence"];
  const createdAt = row["createdAt"];
  const updatedAt = row["updatedAt"];
  if (
    typeof canvasId !== "string" ||
    !CANVAS_ID.test(canvasId) ||
    typeof name !== "string" ||
    typeof type !== "string" ||
    typeof headSequence !== "number" ||
    !Number.isSafeInteger(headSequence) ||
    headSequence < 1 ||
    typeof createdAt !== "number" ||
    typeof updatedAt !== "number"
  )
    return null;
  const memberId =
    typeof row["memberId"] === "string" ? row["memberId"] : `${CANVAS_MEMBER_PREFIX}${canvasId}`;
  const description = row["description"];
  return {
    canvasId,
    memberId,
    name,
    type,
    headSequence,
    createdAt,
    updatedAt,
    ...(typeof description === "string" && description.trim() ? { description } : {}),
  };
}

/** The pages' rows as one list, newest change first, each Canvas once; rows that are not a Canvas are left out. */
export function foldCatalogPages(
  pages: ReadonlyArray<{ readonly canvases: ReadonlyArray<unknown> }>,
): ReadonlyArray<WebHostCanvasEntry> {
  const byId = new Map<string, WebHostCanvasEntry>();
  for (const page of pages)
    for (const row of page.canvases) {
      const entry = webHostCanvasEntry(row);
      if (entry && !byId.has(entry.canvasId)) byId.set(entry.canvasId, entry);
    }
  return [...byId.values()].sort(
    (a, b) => b.updatedAt - a.updatedAt || a.canvasId.localeCompare(b.canvasId),
  );
}

/** The pages after the first, read once each, up to the lens's bound. */
export async function readFollowingPages(
  read: (args: { worldKey: string; cursor: string; limit: number }) => Promise<CatalogPage>,
  worldKey: string,
  first: CatalogPage,
): Promise<{ pages: ReadonlyArray<CatalogPage>; more: boolean }> {
  const pages: CatalogPage[] = [];
  let cursor = first.nextCursor;
  while (cursor !== null && pages.length < CATALOG_PAGES_MAX - 1) {
    const page = await read({ worldKey, cursor, limit: CATALOG_PAGE });
    pages.push(page);
    cursor = page.nextCursor;
  }
  return { pages, more: cursor !== null };
}

/** The World's Canvases this person may read, live: the first page by subscription, the rest read as it changes. */
export function useWebHostCanvasCatalog(worldKey: string | null): WebHostCanvasCatalog {
  const client = useWorldConvexClient();
  const [answer, setAnswer] = useState<{
    readonly for: readonly [ConvexClient, string];
    readonly catalog: WebHostCanvasCatalog;
  } | null>(null);
  useEffect(() => {
    if (worldKey === null || client === null) return;
    const subject = [client, worldKey] as const;
    let stopped = false;
    let generation = 0;
    const unsubscribe = client.onUpdate(
      catalogRef,
      { worldKey, limit: CATALOG_PAGE },
      (first) => {
        if (stopped) return;
        const mine = ++generation;
        void readFollowingPages((args) => client.query(catalogRef, args), worldKey, first)
          .then(({ pages, more }) => {
            if (stopped || mine !== generation) return;
            setAnswer({
              for: subject,
              catalog: { status: "ready", canvases: foldCatalogPages([first, ...pages]), more },
            });
          })
          .catch((error: unknown) => {
            if (stopped || mine !== generation) return;
            // The first page still lists; the pages after it could not be followed this time.
            setAnswer({
              for: subject,
              catalog: {
                status: "ready",
                canvases: foldCatalogPages([first]),
                more: first.nextCursor !== null,
              },
            });
            void error;
          });
      },
      (error) => {
        if (!stopped)
          setAnswer({
            for: subject,
            catalog: { status: "unavailable", reason: worldWords(error) },
          });
      },
    );
    return () => {
      stopped = true;
      unsubscribe();
    };
  }, [client, worldKey]);
  if (worldKey === null) return { status: "loading" };
  if (client === null)
    return { status: "unavailable", reason: "This window is not signed in to the World." };
  return answer !== null && answer.for[0] === client && answer.for[1] === worldKey
    ? answer.catalog
    : { status: "loading" };
}

const text = (value: unknown): string => (typeof value === "string" ? value : "");

/**
 * The Canvases Cleo made or saved in one conversation kept in the World, from its stored events: each neucleos_canvas
 * create or save she called whose result the World recorded, as the conversation's Canvas card (the pure fold Desk and
 * Pocket share, @cleo/shared/canvasToolCard, fed the stored call and result the way Pocket feeds it). Newest last, each
 * exact revision once.
 */
export function canvasCardsOfEvents(
  events: ReadonlyArray<CloudEvent>,
): ReadonlyArray<CanvasToolCardModel> {
  const results = new Map<string, Readonly<Record<string, unknown>>>();
  for (const event of events)
    if (event.kind === "tool-result") results.set(text(event.body["toolCallId"]), event.body);
  const cards: CanvasToolCardModel[] = [];
  const seen = new Set<string>();
  for (const event of events) {
    if (event.kind !== "tool-call") continue;
    const name = text(event.body["name"]);
    const result = results.get(text(event.body["toolCallId"]));
    if (!result || result["isError"] === true || result["ended"] === true) continue;
    let input: unknown = {};
    try {
      input = JSON.parse(text(event.body["argumentsJson"]) || "{}");
    } catch {
      input = {};
    }
    const model = canvasToolCardModel(name, {
      toolName: name,
      input,
      output: { isError: false, text: text(result["text"]) },
    });
    if (!model) continue;
    const key = `${model.canvasId}@${model.sequence}`;
    if (seen.has(key)) continue;
    seen.add(key);
    cards.push(model);
  }
  return cards;
}

/** The conversation's Canvas cards, memoized on the events. */
export function useCanvasCardsOfEvents(
  events: ReadonlyArray<CloudEvent> | null,
): ReadonlyArray<CanvasToolCardModel> {
  return useMemo(() => (events === null ? [] : canvasCardsOfEvents(events)), [events]);
}
